import { Image } from 'expo-image';
import { useLocalSearchParams, useRouter } from 'expo-router';
import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import { Pressable, RefreshControl, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type {
  PartnerSubscriptionCycle,
  PartnerSubscriptionDetail,
} from '@/api/partners/subscriptions/types';
import { useSubscription } from '@/api/partners/subscriptions/use-subscription';
import { useHasOrganization } from '@/core/hooks/use-has-organization';
import {
  capitalizeFirstLetter,
  formatCurrency,
  formatInOriginalOffset,
  formatNumber,
  isDesktop,
} from '@/core/utils';
import { Card, Text } from '@/ui';
import { Chip } from '@/ui/chip';
import DataTable from '@/ui/data-table';
import { Icon } from '@/ui/icon';
import { Location } from '@/ui/icons/location';
import { Loader } from '@/ui/loader';

import {
  NavigationButton,
  NavigationButtonTypes,
} from '../../navigation-button';
import { IdLink, SubscriptionStatusChip } from '../reservation-parts';
import {
  getPartnerReservationStatusChipType,
  getPartnerReservationStatusLabel,
  getRenewalPeriodLabel,
} from '../status-label';

const SUMMARY_DATE_PATTERN = 'MM/dd/yyyy';
const CYCLE_TABLE_DATE_PATTERN = 'MMM dd, yyyy | hh:mm a';
const CYCLE_CARD_DATE_PATTERN = 'MMM dd | hh:mm a';
const CYCLES_PER_PAGE = 4;
const UNLIMITED_MILEAGE_TIER = -1;

const DetailCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <Card>
      <View style={styles.cardHeader}>
        <Text type="title2">{title}</Text>
      </View>

      {children}
    </Card>
  );
};

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.detailRow}>
      <Text type="caption1" style={styles.secondaryText}>
        {label}
      </Text>

      {children}
    </View>
  );
};

/** Dollar amount, highlighted in red while money is owed. */
const BalanceText = ({
  amount,
  type,
}: {
  amount: number;
  type: ComponentProps<typeof Text>['type'];
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <Text type={type} style={amount > 0 ? styles.owedText : undefined}>
      {formatCurrency(amount)}
    </Text>
  );
};

const CycleStatusChip = ({ status }: { status: string }) => (
  <Chip
    type={getPartnerReservationStatusChipType(status)}
    text={getPartnerReservationStatusLabel(status)}
  />
);

const SubscriptionCards = ({
  subscription,
}: {
  subscription: PartnerSubscriptionDetail;
}) => {
  const { styles } = useStyles(stylesheet);
  const { isEVFMC } = useHasOrganization();
  const { car, customer, outstandingBalance } = subscription;
  // Only finished cycles count, same as the average miles figure.
  const totalEarnings = subscription.reservations
    .filter((cycle) => cycle.status === 'completed')
    .reduce((sum, cycle) => sum + cycle.tierCost, 0);

  return (
    <View style={styles.cards}>
      <DetailCard title="Subscription Overview">
        <View style={[styles.cardSection, styles.cardSectionDivider]}>
          <DetailRow label="Status">
            <SubscriptionStatusChip status={subscription.status} />
          </DetailRow>

          <DetailRow label="Start date">
            <Text type="caption1">
              {formatInOriginalOffset(
                subscription.startTime,
                SUMMARY_DATE_PATTERN
              )}
            </Text>
          </DetailRow>

          <DetailRow label="Renewal period">
            <Text type="caption1">
              {getRenewalPeriodLabel(subscription.renewalPeriod)}
            </Text>
          </DetailRow>

          <DetailRow label="Base earnings">
            <Text type="caption1">
              {formatCurrency(subscription.baseEarnings)}
            </Text>
          </DetailRow>

          <DetailRow label="Average miles per period">
            <Text type="caption1">
              {subscription.averageMilesPerPeriod === null
                ? '-'
                : `${formatNumber(subscription.averageMilesPerPeriod)} mi`}
            </Text>
          </DetailRow>

          {isEVFMC ? (
            <>
              <DetailRow label="Billing cadence">
                <Text type="caption1">
                  {subscription.billingCadence
                    ? capitalizeFirstLetter(subscription.billingCadence)
                    : '-'}
                </Text>
              </DetailRow>

              <DetailRow label="Protection">
                <Text type="caption1">{subscription.protection ?? 'None'}</Text>
              </DetailRow>
            </>
          ) : null}
        </View>

        <View style={styles.cardSection}>
          <DetailRow label="Customer">
            <Text type="caption1">
              {`${customer.firstName} ${customer.lastName}`}
            </Text>
          </DetailRow>

          <DetailRow label="Pickup address">
            <Text type="caption1" style={styles.addressText}>
              {subscription.pickupAddress ?? '-'}
            </Text>
          </DetailRow>
        </View>
      </DetailCard>

      <DetailCard title="Car Detail">
        <View style={styles.cardSection}>
          <DetailRow label="Car Model">
            <Text type="caption1">{`${car.make} ${car.model} (${car.year})`}</Text>
          </DetailRow>

          <DetailRow label="Plate">
            <Text type="caption1">{car.plate}</Text>
          </DetailRow>

          <DetailRow label="VIN">
            <Text type="caption1">{car.vin}</Text>
          </DetailRow>
        </View>
      </DetailCard>

      {isEVFMC ? (
        <View style={styles.balanceCards}>
          <View style={styles.balanceCard}>
            <DetailCard title="Outstanding Balance">
              <View style={styles.cardSection}>
                {outstandingBalance.amount > 0 && outstandingBalance.dueDate ? (
                  <Text type="caption2" style={styles.secondaryText}>
                    {`Due ${formatInOriginalOffset(
                      outstandingBalance.dueDate,
                      SUMMARY_DATE_PATTERN
                    )}`}
                  </Text>
                ) : null}

                <BalanceText amount={outstandingBalance.amount} type="title1" />
              </View>
            </DetailCard>
          </View>

          <View style={styles.balanceCard}>
            <DetailCard title="Total Earnings">
              <View style={styles.cardSection}>
                <Text type="caption2" style={styles.secondaryText}>
                  {`Since ${formatInOriginalOffset(
                    subscription.startTime,
                    SUMMARY_DATE_PATTERN
                  )}`}
                </Text>

                <Text type="title1">{formatCurrency(totalEarnings)}</Text>
              </View>
            </DetailCard>
          </View>
        </View>
      ) : null}
    </View>
  );
};

/** One trip per billing cycle: a paginated table on desktop, cards on mobile. */
const BillingCycles = ({ cycles }: { cycles: PartnerSubscriptionCycle[] }) => {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const { isEVFMC } = useHasOrganization();
  // All cycles come with the subscription, so pagination is client-side.
  const [page, setPage] = useState(0);

  return (
    <View style={styles.cycles}>
      <View style={styles.cyclesHeading}>
        <Text type="title2">{`Reservations (${cycles.length})`}</Text>

        <Text type="caption1" style={styles.secondaryText}>
          A new trip ID is created for each billing cycle.
        </Text>
      </View>

      {isDesktop() ? (
        <DataTable
          columns={[
            { key: 'trip-id', title: 'TRIP ID', style: { minWidth: 110 } },
            {
              key: 'start-date',
              title: 'START DATE',
              style: { minWidth: 190 },
            },
            { key: 'status', title: 'STATUS', style: { minWidth: 130 } },
            {
              key: 'mileage-tier',
              title: 'MILEAGE TIER',
              style: { minWidth: 140 },
            },
            { key: 'tier-cost', title: 'TIER COST', style: { minWidth: 120 } },
            ...(isEVFMC
              ? [
                  {
                    key: 'outstanding-balance',
                    title: 'OUTSTANDING BALANCE',
                    style: { minWidth: 180 },
                  },
                ]
              : []),
          ]}
          rows={cycles
            .slice(page * CYCLES_PER_PAGE, (page + 1) * CYCLES_PER_PAGE)
            .map((cycle) => ({
              key: cycle.id,
              id: cycle.id,
              'trip-id': (
                <IdLink
                  id={cycle.id}
                  href={`/partner/reservations/${cycle.id}`}
                />
              ),
              'start-date': (
                <Text type="caption1">
                  {formatInOriginalOffset(
                    cycle.startTime,
                    CYCLE_TABLE_DATE_PATTERN
                  )}
                </Text>
              ),
              status: <CycleStatusChip status={cycle.status} />,
              'mileage-tier': (
                <Text type="caption1">
                  {cycle.mileageTier === UNLIMITED_MILEAGE_TIER
                    ? 'Unlimited'
                    : `${formatNumber(cycle.mileageTier)} mi`}
                </Text>
              ),
              'tier-cost': (
                <Text type="caption1">{formatCurrency(cycle.tierCost)}</Text>
              ),
              'outstanding-balance': (
                <BalanceText
                  amount={cycle.outstandingBalance}
                  type="bodyRegular"
                />
              ),
            }))}
          page={page}
          total={cycles.length}
          rowsPerPage={CYCLES_PER_PAGE}
          onPageChange={setPage}
        />
      ) : (
        <View style={styles.cycleCards}>
          {cycles.map((cycle) => (
            <Pressable
              key={cycle.id}
              onPress={() => {
                router.push(`/partner/reservations/${cycle.id}`);
              }}
            >
              <Card>
                <View style={[styles.cardHeader, styles.cycleHeader]}>
                  <Text type="title2">{`ID: ${cycle.id}`}</Text>

                  <CycleStatusChip status={cycle.status} />
                </View>

                <View style={[styles.cardSection, styles.cycleBody]}>
                  <Icon
                    type="naked"
                    icon={<Location />}
                    fillColor={theme.colors.icon.color}
                    width={16}
                    height={16}
                    style={styles.cycleIcon}
                  />

                  <View>
                    <Text type="bodyRegular">Start date</Text>

                    <Text type="bodyRegular" style={styles.secondaryText}>
                      {formatInOriginalOffset(
                        cycle.startTime,
                        CYCLE_CARD_DATE_PATTERN
                      )}
                    </Text>
                  </View>
                </View>

                {isEVFMC ? (
                  <View style={styles.cycleFooter}>
                    <Text type="caption1" style={styles.secondaryText}>
                      Outstanding balance
                    </Text>

                    <Text type="caption1">
                      {formatCurrency(cycle.outstandingBalance)}
                    </Text>
                  </View>
                ) : null}
              </Card>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
};

export const SubscriptionDetail = () => {
  const { styles } = useStyles(stylesheet);
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { data, isLoading, isRefetching, isError, refetch } = useSubscription({
    variables: { id: Number(id) },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <View style={styles.centered}>
        <Loader style={styles.loader} />
      </View>
    );
  }

  if (isError || !data) {
    return (
      <View style={styles.centered}>
        <Text type="title2">Subscription not found</Text>
      </View>
    );
  }

  const themeName = UnistylesRuntime.themeName as 'dark' | 'light';
  const imageUrl = data.car.images[themeName]?.extraLarge?.[0]?.url;

  const carImage = imageUrl ? (
    <Image style={styles.carImage} source={imageUrl} contentFit="contain" />
  ) : null;
  const cards = <SubscriptionCards subscription={data} />;
  const cycles = <BillingCycles cycles={data.reservations} />;

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={() => {
              refetch();
            }}
          />
        }
      >
        <View style={styles.nav}>
          <NavigationButton
            type={NavigationButtonTypes.back_with_text}
            onPress={() => {
              router.navigate({
                pathname: '/partner/reservations',
                params: { tab: 'subscription' },
              });
            }}
          />

          <Text type="title1">{`Subscription ID: ${data.id}`}</Text>
        </View>

        {isDesktop() ? (
          <View style={styles.mainLayout}>
            <View style={styles.leftColumn}>
              {carImage}

              {cycles}
            </View>

            {cards}
          </View>
        ) : (
          <View style={styles.leftColumn}>
            {carImage}

            {cards}

            {cycles}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    paddingTop: 16,
  },
  centered: {
    flex: 1,
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    flex: 1,
    borderRadius: 16,
  },
  scrollViewContent: {
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 0,
      md: 56,
    },
    flexGrow: 1,
    paddingBottom: 100,
    gap: 32,
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  mainLayout: {
    flexDirection: 'row',
    gap: 32,
    alignItems: 'flex-start',
  },
  leftColumn: {
    flex: 1,
    gap: 32,
  },
  carImage: {
    width: '100%',
    height: 300,
  },
  cards: {
    gap: 16,
    width: {
      xs: '100%',
      md: 420,
    },
  },
  cardHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  cardSection: {
    padding: 16,
    gap: 12,
  },
  cardSectionDivider: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  balanceCards: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    gap: 16,
  },
  balanceCard: {
    flex: {
      md: 1,
    },
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  addressText: {
    flex: 1,
    textAlign: 'right',
  },
  cycles: {
    gap: 16,
  },
  cyclesHeading: {
    gap: 8,
  },
  cycleCards: {
    gap: 16,
  },
  cycleIcon: {
    width: 40,
    height: 40,
  },
  loader: {
    height: 250,
    width: 250,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
  owedText: {
    color: theme.colors.error,
  },
  cycleHeader: {
    gap: 8,
  },
  cycleBody: {
    flexDirection: 'row',
    gap: 8,
  },
  cycleFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
}));
